import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { copsAuthenticatorConstants as constants } from 'utils/app-configs';
import {
  authenticated,
  authRedirectCallBack,
  getAuthenticator,
  getLoginRequest,
  handleAuthResponse,
  isValidToken,
  updateConfigWithRedirectUri,
} from 'utils/cops-authenticator-helper';
import { v4 as uuidv4 } from 'uuid';

/**
 * Initiates the login process using the provided authenticator.
 *
 * This function performs the following steps:
 * 1. Retrieves the authenticator instance.
 * 2. If the authenticator is not available, it exits early.
 * 3. Retrieves the preferred username and authentication configuration from local storage.
 * 4. Constructs the login request using the preferred username and scope
 *    from the authentication configuration.
 * 5. Stores the current pathname in local storage.
 * 6. Handles the redirect promise from the authenticator:
 *    - If no token response is received, it initiates a login redirect with
 *      the constructed login request and prompt.
 *    - If a token response is received, it calls the authentication redirect
 *      callback with the token response.
 */
const login = () => {
  getAuthenticator().then((pca) => {
    if (!pca) {
      return;
    }
    if (localStorage.getItem('auth-interaction-in-progress') === 'true') {
      return; // Exit to avoid the error
    }
    localStorage.setItem('auth-interaction-in-progress', 'true');
    const preferredUserName = localStorage.getItem(`auth-${constants.LOGIN_HINT}`);
    const authConfig = JSON.parse(localStorage.getItem(`auth-${constants.CONFIG}`));

    const loginRequest = getLoginRequest(preferredUserName, authConfig.scopeMSAL);

    localStorage.setItem(`auth-${constants.PATHNAME}`, window.location.pathname);

    pca.handleRedirectPromise().then((tokenResponse) => {
      if (!tokenResponse) {
        pca.loginRedirect(
          {
            ...loginRequest,
            prompt: authConfig.prompt,
          },
        );
      } else {
        authRedirectCallBack(null, tokenResponse, pca);
      }
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    })
      .finally(() => {
        // Reset the flag after the authentication interaction has completed
        localStorage.removeItem('auth-interaction-in-progress');
      });
  });
};

/**
 * Retrieves a cached authentication token from local storage.
 *
 * Steps:
 * 1. Retrieves the authenticator instance.
 * 2. If the authenticator is not available, it exits early.
 * 3. Try to retrieve the access token from local storage.
 * 4. If the token is valid (as determined by `isValidToken`), return the token.
 */
const getCachedToken = () => getAuthenticator().then((pca) => {
  if (!pca) {
    return null;
  }

  try {
    const accessToken = localStorage.getItem(`auth-${constants.ACCESS_TOKEN}`);
    if (isValidToken(accessToken)) {
      return accessToken;
    }
    return null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
    return null;
  }
});

/**
 * Asynchronously retrieves an authentication token.
 *
 * This function attempts to retrieve an authentication token using the following steps:
 * 1. Retrieves the authenticator instance.
 * 2. If the authenticator is not available, it exits early.
 * 3. Check for a cached token and return it if available.
 * 4. Retrieve the preferred username and authentication configuration from local storage.
 * 5. Create a login request object using the preferred username and authentication scope.
 *    To avoid "cache miss" errors, there need to be checked on the account properties
 * 6. Attempt to acquire a token silently using the login request.
 *    loginRequest will contain loginHint property
 *    when applications already have a session / hint acquired(preferredUserName from token)
 *    The request object will contain both scopes and loginHint properties, making
 *    'acquireToken' functionally equivalent to 'ssoSilent'.
 * 7. If silent token acquisition fails due to an interaction required error,
 *    attempt to acquire a token via redirect.
 * 8. If a token is successfully acquired, handle the authentication response and return the token.
 * 9. If all attempts fail, return null.
 */
const getToken = async () => {
  const pca = await getAuthenticator();
  if (!pca) {
    return null;
  }

  const cachedToken = await getCachedToken();
  if (cachedToken) {
    return cachedToken;
  }

  const preferredUserName = localStorage.getItem(`auth-${constants.LOGIN_HINT}`);
  const authConfig = JSON.parse(localStorage.getItem(`auth-${constants.CONFIG}`));

  const loginRequest = getLoginRequest(preferredUserName, authConfig.scopeMSAL);
  loginRequest.account =
    pca.getAccountByUsername(localStorage.getItem(`auth-${constants.ACCOUNT_USERNAME}`));

  if (loginRequest.account) {
    const silentResponse = await pca.acquireTokenSilent(loginRequest).catch(async (error) => {
      localStorage.setItem(`auth-${constants.PATHNAME}`, window.location.pathname);
      if (error instanceof InteractionRequiredAuthError) {
        await pca.acquireTokenRedirect(loginRequest);
      }
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    });
    if (silentResponse) {
      return handleAuthResponse(silentResponse);
    }
  }
  return null;
};

/**
 * Logs out the current user by clearing authentication-related data from local storage
 * and redirecting to the post-logout URI.
 */
const logout = () => {
  getAuthenticator().then((pca) => {
    if (!pca) {
      return null;
    }
    const authConfig = JSON.parse(localStorage.getItem(`auth-${constants.CONFIG}`));
    Object.keys(constants).forEach((key) => {
      localStorage.removeItem(`auth-${constants[key]}`);
    });
    pca.logoutRedirect({
      account: pca.getAccountByUsername(localStorage.getItem(`auth-${constants.ACCOUNT_USERNAME}`)),
      postLogoutRedirectUri: authConfig.postLogoutRedirectUri,
    });
    return null;
  });
};

/**
 * Configures the COPS Authenticator with the provided configuration.
 *
 * Step 1: Validate required parameters
 * Step 2: Retrieve existing authentication version and configuration from localStorage
 * Step 3: Set OAuth version if not already set
 * Step 4: Determine resource URI and OAuth URL based on version
 * Step 5: Define base configuration
 * Step 6: Merge base configuration with client-specific configuration
 * Step 7: Use the new configuration if no existing configuration is found
 */
const configureCopsAuthenticator = (config) => {
  if (!config?.clientId || !config?.tenant) {
    throw new Error('some required parameters are missing');
  }

  let authVersion = localStorage.getItem(`auth-${constants.AUTH_VERSION}`);
  let authConfig = JSON.parse(localStorage.getItem(`auth-${constants.CONFIG}`));

  if (!authVersion && authVersion !== config?.oAuthVersion) {
    const oAuthVersion = config?.oAuthVersion || constants.V2;
    localStorage.setItem(`auth-${constants.AUTH_VERSION}`, oAuthVersion);
    authVersion = oAuthVersion;
  }

  const resourceUri = config?.resourceUri || config?.clientId;
  const oAuthUrl = authVersion === constants.V2 ? 'oauth2/v2.0' : 'oauth2';
  const clientConfig = { clientId: config?.clientId, resourceUri, tenant: config?.tenant };

  const baseConfig = {
    baseUrl: 'https://login.microsoftonline.com',
    authorizeUrl: `${oAuthUrl}/authorize`,
    oAuthVersion: authVersion,
    logoutUrl: `${oAuthUrl}/logout`,
    scopeV1: config?.scope || 'openid',
    scopeV2: config?.scope || `${config?.clientId}/.default`,
    scopeMSAL: config?.scope || [`${config?.clientId}/.default`],
    responseType: 'id_token token',
    responseMode: 'fragment',
    prompt: 'select_account',
    clientRequestId: uuidv4(),
    cacheLocation: 'localStorage',
    enableLogging: true,
    enableAutoRedirect: true,
    client_secret: config?.clientSecret,
    redirectUri: window.location.origin,
    policies: { authorities: {}, names: {} },
    knownAuthorities: [],
    validateAuthority: false,
    postLogoutRedirectUri: window.location.origin,
  };

  const fullConfig = updateConfigWithRedirectUri(Object.assign({}, baseConfig, clientConfig));
  localStorage.setItem(`auth-${constants.CONFIG}`, JSON.stringify(fullConfig));

  if (!authConfig) {
    authConfig = fullConfig;
  }

  authenticated(authVersion, authConfig);
};

export { configureCopsAuthenticator, getCachedToken, getToken, login, logout };

